<script setup>
import { ref, computed, nextTick } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  label: { type: String, required: true },
  value: { type: String, default: '' },
  mono: { type: Boolean, default: false },
  maxlength: { type: Number, default: 50 },
  placeholder: { type: String, default: '—' },
  noDivider: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const isEditing = ref(false);
const editedValue = ref('');
const inputRef = ref(null);

const displayClasses = computed(() => {
  const base = 'text-xs font-medium text-right break-all text-n-slate-12';
  if (props.mono && props.value) {
    return `${base} font-mono bg-n-slate-3 px-1.5 py-0.5 rounded text-n-slate-11 text-[11px] border border-n-weak`;
  }
  return base;
});

const startEdit = () => {
  editedValue.value = props.value || '';
  isEditing.value = true;
  nextTick(() => inputRef.value?.focus());
};

const saveEdit = () => {
  const trimmed = editedValue.value.trim();
  if (trimmed !== (props.value || '')) {
    emit('save', trimmed);
  }
  isEditing.value = false;
};

const cancelEdit = () => {
  isEditing.value = false;
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 py-1.5 px-0 group"
    :class="{ 'border-b border-n-weak/50': !noDivider }"
  >
    <span class="text-[11px] text-n-slate-10 shrink-0">{{ label }}</span>

    <!-- DISPLAY MODE -->
    <div
      v-if="!isEditing"
      class="flex items-center gap-1 cursor-pointer"
      @click="startEdit"
    >
      <span :class="displayClasses">{{ value || placeholder }}</span>
      <span
        class="i-lucide-pencil w-3 h-3 text-n-slate-8 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </div>

    <!-- EDIT MODE -->
    <div v-else v-on-click-outside="cancelEdit" class="flex items-center gap-1">
      <input
        ref="inputRef"
        v-model="editedValue"
        type="text"
        :maxlength="maxlength"
        class="w-full max-w-[170px] text-[11px] font-mono bg-n-solid-2 border border-n-blue-7 rounded px-1.5 py-0.5 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
        @keydown.enter="saveEdit"
        @keydown.escape="cancelEdit"
      />
      <button
        class="text-n-teal-9 hover:text-n-teal-11 transition-colors"
        @click="saveEdit"
      >
        <span class="i-lucide-check w-3.5 h-3.5" />
      </button>
      <button
        class="text-n-slate-8 hover:text-n-slate-11 transition-colors"
        @click="cancelEdit"
      >
        <span class="i-lucide-x w-3.5 h-3.5" />
      </button>
    </div>
  </div>
</template>
